import { useTranslation } from "react-i18next";
import { useState, useRef } from "react";
import Seo from "../../components/Seo/Seo";
import "./Investments.css";
import "../Home/Home.css"
export default function Investments() {
  const { t } = useTranslation("common");
  const [activeInvestment, setActiveInvestment] = useState(null);
  const [fullscreenVideo, setFullscreenVideo] = useState(null);
  const videoRefs = useRef([]);

  const investments = [
    {
      id: 1,
      title: t("investments.items.1.title"),
      shortDescription: t("investments.items.1.shortDescription"),
      mediaType: "video",
      media: "/media/investments/1.mp4",
      thumbnail: "/images/investments/invest-1.png",

      details: {
        type: t("investments.items.1.details.type"),
        description: t("investments.items.1.details.description"),
        location: t("investments.items.1.details.location"),
        investmentRange: t("investments.items.1.details.investmentRange"),
        expectedReturn: t("investments.items.1.details.expectedReturn"),
        whatsapp: "966535531261",
      },
    }
  ];

  const handleVideoHover = (index, isHovering) => {
    const video = videoRefs.current[index];
    if (!video) return;

    if (isHovering) {
      video.currentTime = 0;
      video
        .play()
        .catch(() => { });
    } else {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <>
      <Seo
        title={t("investments.seo.title")}
        description={t("investments.seo.description")}
        path="/investments"
      />

      {/* HERO */}
      <section className="investmentsHero">
        <div className="investmentsHero__overlay"></div>
        <div className="container">
          <h1 className="investmentsHero__title">{t("investments.hero.title")}</h1>
          <p className="investmentsHero__subtitle">
            {t("investments.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* LIST */}
      <section className="investmentsSection">
        <div className="container">
          <div className="investmentsGrid">
            {investments.map((item, index) => (
              <div 
                key={item.id} 
                className="investmentCard"
                style={{
                  animationDelay: `${index * 0.15}s`
                }}
              >
                <div 
                  className="investmentCard__media"
                  onMouseEnter={() => item.mediaType === 'video' && handleVideoHover(index, true)}
                  onMouseLeave={() => item.mediaType === 'video' && handleVideoHover(index, false)}
                >
                  {item.mediaType === "video" ? (
                    <>
                      <video
                        ref={(el) => (videoRefs.current[index] = el)}
                        loop
                        playsInline
                        preload="metadata"
                        onLoadedData={(e) => {
                          e.target.currentTime = 0.01;
                        }}
                      >
                        <source src={item.media} type="video/mp4" />
                      </video>
                      <div className="investmentCard__playIcon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <button
                        className="investmentCard__fullscreen"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFullscreenVideo(item);
                        }}
                      >
                        ⛶
                      </button>
                    </>
                  ) : (
                    <img src={item.media} alt={item.title} />
                  )}
                </div>

                <div className="investmentCard__content">
                  <h3 className="investmentCard__title">{item.title}</h3>
                  <p className="investmentCard__description">
                    {item.shortDescription}
                  </p>

                  <button
                    className="investmentCard__link"
                    onClick={() => setActiveInvestment(item)}
                  >
                    {t("investments.modal.viewDetails")}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FULLSCREEN VIDEO MODAL */}
      {fullscreenVideo && (
        <div className="videoOverlay" onClick={() => setFullscreenVideo(null)}>
          <button
            className="videoOverlay__close"
            onClick={() => setFullscreenVideo(null)}
          >
            ✕
          </button>
          <video
            src={fullscreenVideo.media}
            autoPlay
            controls
            playsInline
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* DETAILS MODAL */}
      {activeInvestment && (
        <div className="investmentModal">
          <div
            className="investmentModal__overlay"
            onClick={() => setActiveInvestment(null)}
          />

          <div className="investmentModal__content">
            <button
              className="investmentModal__close"
              onClick={() => setActiveInvestment(null)}
            >
              ✕
            </button>

            <h2 className="investmentModal__title">{activeInvestment.title}</h2>
            <p className="investmentModal__type">
              {t("investments.modal.opportunityType")} {activeInvestment.details.type}
            </p>

            <div className="investmentModal__description">
              {activeInvestment.details.description}
            </div>

            <div className="investmentModal__info">
              <div className="investmentModal__infoItem">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <div>
                  <strong>{t("investments.modal.location")}</strong> {activeInvestment.details.location}
                </div>
              </div>
              <div className="investmentModal__infoItem">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                </svg>
                <div>
                  <strong>{t("investments.modal.investmentSize")}</strong>{" "}
                  {activeInvestment.details.investmentRange}
                </div>
              </div>
              <div className="investmentModal__infoItem">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                </svg>
                <div>
                  <strong>{t("investments.modal.expectedReturn")}</strong>{" "}
                  {activeInvestment.details.expectedReturn}
                </div>
              </div>
            </div>

            <a
              className="investmentModal__whatsapp"
              href={`https://wa.me/${activeInvestment.details.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t("investments.modal.whatsapp")}
            </a>
          </div>
        </div>
      )}
    </>
  );
}